import { useEffect, useState } from "react";
import { AlertTriangle, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { eur } from "./docUtils";

const KIND = {
  devis: { label: "Devis", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  facture: { label: "Facture", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  paiement: { label: "Paiement", cls: "bg-green-50 text-green-700 border-green-200" },
  avoir: { label: "Avoir", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function ClientFinancials({ clientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (clientId) api.get(`/accounting/client-financials/${clientId}`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);
  if (!data) return null;

  const cards = [
    { l: "Facturé ce mois", v: eur(data.facture_mois), t: "fin-facture" },
    { l: "Encaissé ce mois", v: eur(data.encaisse_mois), t: "fin-encaisse" },
    { l: "Temps passé", v: `${data.heures.toFixed(1).replace(".", ",")} h`, t: "fin-temps" },
    { l: "Taux horaire réel", v: data.taux_reel != null ? `${eur(data.taux_reel)}/h` : "—", t: "fin-taux", warn: data.sous_objectif },
    { l: "Coût temps estimé", v: eur(data.cout_estime), t: "fin-cout" },
    { l: "Rentabilité", v: data.rentabilite_pct != null ? `${data.rentabilite_pct} %` : "—", t: "fin-rentabilite" },
  ];

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 max-w-2xl space-y-5" data-testid="client-financials">
      <div>
        <h3 className="font-heading font-bold mb-1 flex items-center gap-2">
          <Clock3 className="w-4 h-4 text-neutral-400" /> Rentabilité en temps réel
        </h3>
        <p className="text-sm text-neutral-500">Comptabilité × chronomètre — recalculé à chaque temps enregistré.</p>
      </div>

      {data.sous_objectif && (
        <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm" data-testid="fin-alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Rentabilité à surveiller : {eur(data.taux_reel)}/h, inférieur à l'objectif de {eur(data.target_rate)}/h.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.t} className="bg-neutral-50 border border-neutral-100 rounded-lg p-3" data-testid={c.t}>
            <p className="text-[11px] uppercase tracking-wider text-neutral-400">{c.l}</p>
            <p className={`font-heading font-bold text-lg mt-0.5 ${c.warn ? "text-red-600" : ""}`}>{c.v}</p>
          </div>
        ))}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wider text-neutral-400 mb-2">Historique financier</p>
        {data.history.length === 0 && <p className="text-sm text-neutral-400">Aucun document pour ce client.</p>}
        <div className="space-y-1.5" data-testid="fin-history">
          {data.history.map((h, i) => (
            <div key={i} className="flex items-center gap-3 text-sm border border-neutral-100 rounded-lg px-3 py-2">
              <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${KIND[h.kind].cls}`}>
                {KIND[h.kind].label}
              </span>
              <span className="flex-1 min-w-0 truncate">{h.label}</span>
              <span className="text-xs text-neutral-400 shrink-0">{h.date}</span>
              <span className={`font-medium shrink-0 ${h.amount < 0 ? "text-red-600" : h.kind === "paiement" ? "text-green-700" : ""}`}>
                {h.amount != null ? `${h.amount < 0 ? "-" : ""}${eur(Math.abs(h.amount))}` : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
